import React from 'react';
import styled from 'styled-components';
import { Spin, Button, Skeleton, Result } from 'antd';
import { ArrowDownOutlined, FormOutlined, ReloadOutlined } from '@ant-design/icons';
import { PromptTemplateCreatedByType } from '../../../../ai';
import { useConversationScroll, useConversationEmpty } from './hooks';
import { SuggestionItem, GreetingMessage } from '../../components';
import { usePromptTemplates } from '../../hooks/use-prompt-templates.hook';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var ConversationWrapper = styled.div.withConfig({
  displayName: "conversationcontainer__ConversationWrapper",
  componentId: "sc-ue172i-0"
})(["flex:1;min-height:0;display:flex;flex-direction:column;position:relative;"]);
var ConversationScroll = styled.div.withConfig({
  displayName: "conversationcontainer__ConversationScroll",
  componentId: "sc-ue172i-1"
})(["overflow-y:auto;flex:1;min-height:0;display:flex;flex-direction:column;padding:16px 16px 64px 16px;background-color:#fff;"]);
var EmptyState = styled.div.withConfig({
  displayName: "conversationcontainer__EmptyState",
  componentId: "sc-ue172i-2"
})(["flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;text-align:center;color:#999;font-size:14px;"]);
var LoadingState = styled.div.withConfig({
  displayName: "conversationcontainer__LoadingState",
  componentId: "sc-ue172i-3"
})(["flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;color:#666;font-size:14px;"]);
var ScrollToBottomButton = styled(Button).withConfig({
  displayName: "conversationcontainer__ScrollToBottomButton",
  componentId: "sc-ue172i-4"
})(["position:absolute;bottom:80px;right:24px;box-shadow:0 2px 8px rgba(0,0,0,0.15);z-index:10;"]);
var LoadMoreAtTop = styled.div.withConfig({
  displayName: "conversationcontainer__LoadMoreAtTop",
  componentId: "sc-ue172i-5"
})(["display:flex;align-items:center;justify-content:center;gap:8px;padding:12px 16px;font-size:13px;color:#666;"]);
var GreetingEmptyState = styled.div.withConfig({
  displayName: "conversationcontainer__GreetingEmptyState",
  componentId: "sc-ue172i-6"
})(["flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;align-items:center;padding:24px;max-width:600px;margin:0 auto;width:100%;"]);
var GreetingContent = styled.div.withConfig({
  displayName: "conversationcontainer__GreetingContent",
  componentId: "sc-ue172i-7"
})(["margin:auto 0;width:100%;display:flex;flex-direction:column;align-items:center;"]);
var SuggestionsGrid = styled.div.withConfig({
  displayName: "conversationcontainer__SuggestionsGrid",
  componentId: "sc-ue172i-8"
})(["display:grid;grid-template-columns:1fr;gap:12px;width:100%;"]);
export var ConversationContainer = function ConversationContainer(_ref) {
  var children = _ref.children,
      messagesCount = _ref.messagesCount,
      isStreaming = _ref.isStreaming,
      isConversationLoading = _ref.isConversationLoading,
      isLoadingMore = _ref.isLoadingMore,
      onLoadMore = _ref.onLoadMore,
      emptyMessage = _ref.emptyMessage,
      onSuggestionClick = _ref.onSuggestionClick,
      _ref$streamingContent = _ref.streamingContentSignal,
      streamingContentSignal = _ref$streamingContent === void 0 ? 0 : _ref$streamingContent,
      formId = _ref.formId;

  var _usePromptTemplates = usePromptTemplates({
    resourceType: 'category',
    resourceId: formId,
    createdByType: PromptTemplateCreatedByType.Admin
  }),
      promptTemplates = _usePromptTemplates.promptTemplates,
      isLoadingPromptTemplates = _usePromptTemplates.isLoading,
      isErrorPromptTemplates = _usePromptTemplates.isError,
      retryPromptTemplates = _usePromptTemplates.retry;

  var suggestionItems = promptTemplates.map(function (promptTemplate) {
    return {
      title: promptTemplate.title,
      description: promptTemplate.description,
      icon: /*#__PURE__*/_jsx(FormOutlined, {}),
      promptText: promptTemplate.template
    };
  });

  var _useConversationEmpty = useConversationEmpty({
    messagesCount: messagesCount,
    isStreaming: isStreaming,
    isLoading: isConversationLoading,
    emptyMessage: emptyMessage
  }),
      isEmptyMessage = _useConversationEmpty.isEmptyMessage,
      defaultEmpty = _useConversationEmpty.emptyMessage;

  var contentLength = messagesCount;

  var _useConversationScrol = useConversationScroll({
    contentLength: contentLength,
    isStreaming: isStreaming,
    smooth: true,
    onScrollToTop: onLoadMore,
    streamingContentSignal: streamingContentSignal
  }),
      scrollRef = _useConversationScrol.scrollRef,
      showScrollToBottom = _useConversationScrol.showScrollToBottom,
      scrollToBottom = _useConversationScrol.scrollToBottom,
      handleScroll = _useConversationScrol.handleScroll;

  if (isConversationLoading) {
    return /*#__PURE__*/_jsxs(LoadingState, {
      children: [/*#__PURE__*/_jsx(Spin, {
        size: "large"
      }), /*#__PURE__*/_jsx("span", {
        style: {
          marginTop: 12
        },
        children: "Loading conversation..."
      })]
    });
  }

  if (isEmptyMessage) {
    var shouldShowGreeting = suggestionItems && suggestionItems.length > 0 && onSuggestionClick;
    var shouldShowGreetingSkeleton = isLoadingPromptTemplates && !shouldShowGreeting;
    return /*#__PURE__*/_jsx(GreetingEmptyState, {
      children: /*#__PURE__*/_jsxs(GreetingContent, {
        children: [/*#__PURE__*/_jsx(GreetingMessage, {}), /*#__PURE__*/_jsx(SuggestionsGrid, {
          children: shouldShowGreetingSkeleton ? Array.from({
            length: 4
          }).map(function (_, index) {
            return /*#__PURE__*/_jsx(Skeleton.Button, {
              active: true,
              block: true,
              style: {
                height: 88,
                borderRadius: 8
              }
            }, index);
          }) : isErrorPromptTemplates ? /*#__PURE__*/_jsx(Result, {
            status: "warning",
            title: "Unable to load suggestions",
            subTitle: "Something went wrong while loading suggestion templates.",
            extra: /*#__PURE__*/_jsx(Button, {
              icon: /*#__PURE__*/_jsx(ReloadOutlined, {}),
              onClick: retryPromptTemplates,
              children: "Retry"
            })
          }) : shouldShowGreeting ? suggestionItems.map(function (item, index) {
            return /*#__PURE__*/_jsx(SuggestionItem, {
              title: item.title,
              description: item.description,
              icon: item.icon,
              promptText: item.promptText,
              onClick: onSuggestionClick
            }, index);
          }) : null
        })]
      })
    });
  }

  return /*#__PURE__*/_jsxs(ConversationWrapper, {
    children: [/*#__PURE__*/_jsxs(ConversationScroll, {
      ref: scrollRef,
      onScroll: handleScroll,
      children: [isLoadingMore && /*#__PURE__*/_jsxs(LoadMoreAtTop, {
        children: [/*#__PURE__*/_jsx(Spin, {
          size: "small"
        }), "Loading older messages..."]
      }), children]
    }), showScrollToBottom && /*#__PURE__*/_jsx(ScrollToBottomButton, {
      type: "primary",
      shape: "circle",
      icon: /*#__PURE__*/_jsx(ArrowDownOutlined, {}),
      onClick: scrollToBottom
    })]
  });
};